﻿import React, {useState, useMemo} from 'react';
import {SecurSpaceReportContainer} from "../../components/common";
import {SecurSpaceReportTitle} from "../../components/common";
import ReportDataSummary from "../../components/report/redesigned/ReportDataSummary";
import {PersonAndBoxMessage} from "../../components/messages/PersonAndBoxMessage";
import {TablePagination, Grid} from "@mui/material";
import {
  SupplierCurrentInventoryCardListWithLoader
} from "../../components/supplier/currentInventoryReport/SupplierCurrentInventoryCardList";
import {
  SupplierCurrentInventorySortFilter
} from "../../components/supplier/currentInventoryReport/SupplierCurrentInventorySortFilter";
import {useBindSearchParam} from "../../hooks/useBindSearchParam";
import {useSupplierCurrentInventories} from "../../hooks/supplier/currentInventory/useSupplierCurrentInventories";
import {useSnackbarDisplayOnError} from "../../hooks/error/useSnackbarDisplayOnError";
import {useSnackbar} from "../../context/snackbar-context";
import useDebounce from "../../hooks/useDebounce";
import {SupplierInventorySortByType} from "../../types/supplier/SupplierInventorySortByType";
import {SortDirType} from "../../types/common/SortDirType";
import {useSimpleTablePaginationHandlers} from "../../hooks/pagination/useSimpleTablePaginationHandlers";
import {CheckOutAsset, EditAsset, ModalType, modalTypes} from '../../components/supplier/currentInventoryReport/data';
import SupplierCurrentInventoryDialogs
  from '../../components/supplier/currentInventoryReport/actions/SupplierCurrentInventoryDialogs';
import {InventoryReportViewVO} from '../../types/model/valueObject/InventoryReportViewVO';
import {checkOutInventory, editInventory} from '../../requests/inventory-requests';
import saveGallery from '../../components/supplier/currentInventoryReport/actions/galleryActionsUtil';
import {getErrorMessageForNonStandardAndStandardResponse} from '../../util/NetworkErrorUtil';
import SecurSpaceContainer from "../../components/common/SecurSpaceContainer";
import {SecurSpaceReportPaginationContainer} from "../../components/common";
import {useBoundQueryDownloadUrl} from "../../hooks/report/useBoundQueryDownloadUrl";
import {supplierCurrentInventoryReportEndpoints} from "../../requests/api-endpoints";
import DownloadCSVButton from "../../components/report/export/DownloadCSVButton";
import type { Account } from '../../types/Account';
import {useSupplierCurrentInventoryExpansion} from '../../hooks/supplier/currentInventory/useSupplierCurrentInventoryExpansion';
import MobileAppPromo from "../../components/mobileAppPromo/MobileAppPromo";
import {useFeatureFlags} from "../../hooks/routing/useFeatureFlags";

const DEFAULT_PAGE = 0;
const DEFAULT_SIZE = 10;

type SupplierCurrentInventoryReportProps = {
  account: Pick<Account, 'loggedInUsername' | 'userType'>,
}

const SupplierCurrentInventoryReport = ({account}: SupplierCurrentInventoryReportProps) => {
  const { snackbarShowMessage } = useSnackbar();
  const { mobileAppBanner } = useFeatureFlags();
  // Search params
  const [page, setPage] = useBindSearchParam('page', DEFAULT_PAGE.toString());
  const [size, setSize] = useBindSearchParam('size', DEFAULT_SIZE.toString());

  const defaultModal = {open: false, type: modalTypes.CHECKOUT};

  const [locationId, setLocationId] = useBindSearchParam<string>('locationId', '');
  const [buyerId, setBuyerId] = useBindSearchParam<string>('buyerId', '');
  const [equipmentNumber, setEquipmentNumber] = useBindSearchParam<string>('equipmentNumber', '');
  const [bookingNumber, setBookingNumber] = useBindSearchParam<string>('bookingNumber', '');
  const [sortBy, setSortBy] = useBindSearchParam<SupplierInventorySortByType>('sortBy', 'CHECK_IN_DATE');
  const [sortDir, setSortDir] = useBindSearchParam<SortDirType>('sortDir', 'desc');

  const [openModal, setOpenModal] = useState<ModalType>(defaultModal);
  const [assetBeingActioned, setAssetBeingActioned] = useState<InventoryReportViewVO | null>(null);
  const [imageDropZone, setImageDropZone] = useState<any>();
  const [checkOutLoading, setCheckOutLoading] = useState(false);

  // debounced values so apis don't get spammed while typing
  const debouncedBookingNumber = useDebounce(bookingNumber, 250);
  const debouncedEquipmentNumber = useDebounce(equipmentNumber, 250);

  // api hooks
  const { currentInventories, pageInfo, isLoading, error, doRequest } = useSupplierCurrentInventories({ page, size, locationId, buyerId, equipmentNumber: debouncedEquipmentNumber, bookingNumber: debouncedBookingNumber, sortBy, sortDir });

  // conditional display hooks
  const reportTitle = !isLoading && locationId && currentInventories?.length === 1 ? currentInventories[0].locationName : 'All';

  // error hooks
  useSnackbarDisplayOnError({error, snackbarShowMessage});

  //csv params
  const csvDownloadParams = useMemo(() => {
    return { locationId, buyerId, equipmentNumber: debouncedEquipmentNumber, bookingNumber: debouncedBookingNumber, sortBy, sortDir }
  }, [ locationId, buyerId, debouncedEquipmentNumber, debouncedBookingNumber, sortBy, sortDir ]);

  // conditional display hooks
  const {
    expandByIndex,
    handleToggleCollapse,
    expandedItems,
    setExpandedItems,
    setExpandedAll,
    handleExpandAll
  } = useSupplierCurrentInventoryExpansion({ currentInventories });

  const hasExpandedItems = Object.keys(expandedItems).length > 0;

  const {
    handlePageChange,
    handleRowsPerPageChange,
  } = useSimpleTablePaginationHandlers({ setPage, setSize });

  // dialog handlers
  const handleCloseDialogs = () => {
    setOpenModal(defaultModal);
    setAssetBeingActioned(null);
  };

  const handleCheckOutAsset = (asset: CheckOutAsset) => {
    setCheckOutLoading(true);
    checkOutInventory(asset)
      .then((data: {body: CheckOutAsset}) => {
        if (data?.body && imageDropZone?.files?.length) {
          saveGallery(data.body, imageDropZone, snackbarShowMessage)
        }
        snackbarShowMessage('Successfully checked out inventory.', 'success');
        doRequest();
      })
      .catch((err: any) => snackbarShowMessage(getErrorMessageForNonStandardAndStandardResponse(err), 'error'))
      .finally(() => {
        setCheckOutLoading(false);
        handleCloseDialogs();
      });
  };

  const handleEditAsset = (asset: EditAsset) => {
    editInventory(asset)
      .then(() => {
        snackbarShowMessage('Successfully saved inventory changes.', 'success')
        doRequest();
      })
      .catch((err: any) => snackbarShowMessage(getErrorMessageForNonStandardAndStandardResponse(err), 'error'))
      .finally(() => {
        handleCloseDialogs();
      });
  };

  return <SecurSpaceContainer>
    <SecurSpaceReportContainer>
      <SecurSpaceReportTitle>Current Inventory</SecurSpaceReportTitle>
      {mobileAppBanner && <MobileAppPromo />}
      <SupplierCurrentInventorySortFilter
        buyerId={buyerId}
        onBuyerChangeId={setBuyerId}
        locationId={locationId}
        onLocationChangeId={setLocationId}
        equipmentNumber={equipmentNumber}
        onEquipmentNumberChange={setEquipmentNumber}
        bookingNumber={bookingNumber}
        onBookingNumberChange={setBookingNumber}
        sort={sortBy}
        onSortChange={setSortBy}
        sortDir={sortDir}
        onSortDirChange={setSortDir}
      />
      <ReportDataSummary count={pageInfo?.count || 0}
                         title={reportTitle}
                         isShowCollapseButton={true}
                         hasExpandedItems={hasExpandedItems}
                         setExpandedItems={setExpandedItems}
                         setExpandedAll={setExpandedAll}
                         handleExpandAll={handleExpandAll}
      >
        <Grid item xs lg={3}>
          <DownloadCSVButton
            className={'w-100'}
            title='Download Report'
            reportUrl={useBoundQueryDownloadUrl({
              endpoint: supplierCurrentInventoryReportEndpoints.report,
              query: csvDownloadParams
            })}
          />
        </Grid>
      </ReportDataSummary>
      {
        !error && <SupplierCurrentInventoryCardListWithLoader
          isLoading={isLoading}
          currentInventories={currentInventories}
          expandByIndex={expandByIndex}
          onToggleExpand={handleToggleCollapse}
          setOpenModal={setOpenModal}
          setAssetBeingActioned={setAssetBeingActioned}
          account={account}
        />
      }
      { !isLoading && !error && currentInventories && currentInventories.length === 0 && <PersonAndBoxMessage/>}
      {
        assetBeingActioned &&
          <SupplierCurrentInventoryDialogs
            openDialog={openModal}
            assetBeingActioned={assetBeingActioned}
            onCloseDialogs={handleCloseDialogs}
            onCheckOutAsset={handleCheckOutAsset}
            onEditAsset={handleEditAsset}
            setImageDropZone={setImageDropZone}
            account={account}
            checkOutLoading={checkOutLoading}
          />
      }
      <SecurSpaceReportPaginationContainer>
        <TablePagination
          component={'div'}
          count={pageInfo?.count || 0}
          page={pageInfo?.page || DEFAULT_PAGE}
          onPageChange={handlePageChange}
          rowsPerPage={pageInfo?.rowsPerPage || DEFAULT_SIZE}
          onRowsPerPageChange={handleRowsPerPageChange}
        />
      </SecurSpaceReportPaginationContainer>
    </SecurSpaceReportContainer>
  </SecurSpaceContainer>
}

export default SupplierCurrentInventoryReport;
